/**
 * Activity Area Chart - Shows stacked area visualization
 */

import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart';
import { auditChartConfig } from '../chart-config';
import type { TimelineBucket } from '@/types/reports';

interface ActivityAreaChartProps {
  data: TimelineBucket[];
  breakdownBy?: 'action' | 'entityType';
  className?: string;
}

export function ActivityAreaChart({ data, breakdownBy, className }: ActivityAreaChartProps) {
  // Format and flatten data for stacked area
  const formattedData = data.map((bucket) => ({
    dateLabel: new Date(bucket.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    total: bucket.total,
    ...bucket.breakdown,
  }));

  // Get unique breakdown keys
  const breakdownKeys = breakdownBy
    ? [...new Set(data.flatMap((b) => Object.keys(b.breakdown || {})))]
    : [];

  return (
    <ChartContainer config={auditChartConfig} className={className}>
      <AreaChart data={formattedData} accessibilityLayer>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="dateLabel"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
        />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} />
        <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
        {breakdownBy && breakdownKeys.length > 0 ? (
          <>
            <ChartLegend content={<ChartLegendContent />} />
            {breakdownKeys.map((key) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                fill={`var(--color-${key})`}
                stroke={`var(--color-${key})`}
                fillOpacity={0.4}
                stackId="a"
              />
            ))}
          </>
        ) : (
          <Area
            type="monotone"
            dataKey="total"
            fill="var(--color-total)"
            stroke="var(--color-total)"
            fillOpacity={0.4}
          />
        )}
      </AreaChart>
    </ChartContainer>
  );
}
